import { useState, useEffect, useMemo } from "react";
import StepLayout from "./StepLayout";
import NavFooter from "./NavFooter";
import PatientCard from "./PatientCard";
import styles from "./StepSymptomsCluster.module.css";

import { LOCATION_CATEGORIES } from "./SelectSymptomsConstants";

/* ── Chip ───────────────────────────────────── */
function Chip({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.chip}
    >
      {label}
    </button>
  );
}

export default function StepSymptomsCluster({ patient, onNext, onBack }) {

  /* Load selected */
  const stored = JSON.parse(localStorage.getItem("selectedSymptoms") || "[]");
  const initialSymptoms = stored.map(s => s.label || s);

  const [selected, setSelected] = useState(new Set(initialSymptoms));

  /* Persist */
  useEffect(() => {
    const arr = [...selected].map(label => ({ label }));
    localStorage.setItem("selectedSymptoms", JSON.stringify(arr));
  }, [selected]);

  /* ── RELATED SYMPTOMS ── */
  const relatedClusters = useMemo(() => {
    if (selected.size === 0) return [];

    return LOCATION_CATEGORIES
      .map(cat => {
        const hasMatch = cat.symptoms.some(sym => selected.has(sym));
        if (!hasMatch) return null;

        const suggestions = cat.symptoms.filter(sym => !selected.has(sym));
        if (suggestions.length === 0) return null;

        return {
          title: cat.label,
          items: suggestions,
        };
      })
      .filter(Boolean);

  }, [selected]);

  /* toggle */
  const toggle = (item) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(item) ? next.delete(item) : next.add(item);
      return next;
    });
  };

  const selectedArr = [...selected];

  /* ── MEDICAL HISTORY ── */
  const storedHistory = JSON.parse(localStorage.getItem("medicalHistory") || "{}");

  const mapPreScreenToHistory = (data) => {
    if (!data) return {};
    return {
      condition: data.hasCondition === "Yes" ? data.diagnosed || true : false,
      takingMeds: data.takingMeds === "Yes",
      allergy: data.hasAllergy,
      surgery: data.hasSurgery === "Yes" ? data.surgeryWhen || true : false,
    };
  };

  const historyData = mapPreScreenToHistory(storedHistory);

  return (
    <StepLayout
      title="Associated Symptoms"
      subtitle="We found more symptoms related to your selection"
      icon="🧠"
      footer={
        <NavFooter
          onBack={onBack}
          onNext={() => onNext(selectedArr)}
          nextLabel="Continue"
          disableNext={selectedArr.length === 0}
        />
      }
    >
      <div className={styles.wrapper}>

        {/* ── LEFT SIDE ── */}
        <div className={styles.left}>

          {/* RELATED */}
          <div className={styles.card}>
            <h3 className={styles.title}>Related Symptoms</h3>

            {relatedClusters.length === 0 ? (
              <p className={styles.empty}>Select symptoms to see suggestions</p>
            ) : (
              relatedClusters.map(group => (
                <div key={group.title} className={styles.cluster}>
                  <p className={styles.clusterTitle}>{group.title}</p>

                  <div className={styles.chipContainer}>
                    {group.items.map(item => (
                      <Chip
                        key={item}
                        label={item}
                        onClick={() => toggle(item)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* SELECTED */}
          <div className={styles.card}>
            <div className={styles.selectedHeader}>
              <h3 className={styles.title}>
                Selected Symptoms ({selectedArr.length})
              </h3>

              {selectedArr.length > 0 && (
                <button
                  className={styles.clearBtn}
                  onClick={() => setSelected(new Set())}
                >
                  Clear all
                </button>
              )}
            </div>

            {selectedArr.length > 0 ? (
              <div className={styles.selectedChips}>
                {selectedArr.map(s => (
                  <span key={s} className={styles.selectedChip}>
                    {s}
                    <button
                      onClick={() => toggle(s)}
                      className={styles.removeBtn}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className={styles.empty}>No symptoms selected</p>
            )}
          </div>

        </div>

        {/* ── RIGHT SIDE ── */}
        <div className={styles.right}>

          <PatientCard patient={patient} size={130} />

          {/* Medical History */}
          <div className={styles.historyCard}>
            <h4 className={styles.historyTitle}>Medical History</h4>

            <ul className={styles.historyList}>
              {Object.entries(historyData).length > 0 ? (
                Object.entries(historyData).map(([key, value], i) => {
                  let label = "";
                  let icon = "?";

                  switch (key) {
                    case "condition":
                      label = value && value !== true
                        ? `Diagnosed with ${value}`
                        : "Has medical condition";
                      icon = value ? "✔" : "✖";
                      break;

                    case "takingMeds":
                      label = "Currently taking medications";
                      icon = value ? "✔" : "✖";
                      break;

                    case "allergy":
                      label = "Has allergies";
                      icon = value === "Yes" ? "✔" : value === "No" ? "✖" : "?";
                      break;

                    case "surgery":
                      label = value && value !== true
                        ? `Surgery: ${value}`
                        : "Past surgery";
                      icon = value ? "✔" : "✖";
                      break;

                    default:
                      label = key;
                  }

                  return (
                    <li key={i} className={styles.historyItem}>
                      <span className={styles.historyDot}>{icon}</span>
                      {label}
                    </li>
                  );
                })
              ) : (
                <li className={styles.historyEmpty}>
                  No history available
                </li>
              )}
            </ul>
          </div>

        </div>
      </div>
    </StepLayout>
  );
}

// import { useState } from "react";
// import StepLayout from "./StepLayout";
// import NavFooter from "./NavFooter";
// import PatientCard from "./PatientCard";
// import styles from "./StepSymptomsCluster.module.css";


// const SYMPTOM_CLUSTERS = [
//   {
//     title: "Pain Related",
//     items: ["Back pain", "Joint pain", "Neck stiffness"]
//   },
//   {
//     title: "Digestive Issues",
//     items: ["Vomiting", "Nausea", "Loss of appetite"]
//   },
//   {
//     title: "General Symptoms",
//     items: ["Fever", "Fatigue", "Weakness"]
//   },
//   {
//     title: "Respiratory",
//     items: ["Shortness of breath", "Chest tightness"]
//   }
// ];

// /* ── Chip ───────────────────────────────────── */
// function Chip({ label, selected, onClick }) {
//   return (
//     <button
//       type="button"
//       onClick={onClick}
//       className={`${styles.chip} ${selected ? styles.chipActive : ""}`}
//     >
//       {label}
//     </button>
//   );
// }

// /* ── Main ───────────────────────────────────── */
// export default function StepSymptomsCluster({ patient, initial, onNext, onBack }) {
//   const [selected, setSelected] = useState(new Set(initial || []));

//   const storedHistory = JSON.parse(localStorage.getItem("medicalHistory") || "{}");

//   const mapPreScreenToHistory = (data) => {
//     if (!data) return {};

//     return {
//       condition: data.hasCondition === "Yes" ? data.diagnosed || true : false,
//       takingMeds: data.takingMeds === "Yes",
//       allergy: data.hasAllergy,
//       surgery: data.hasSurgery === "Yes" ? data.surgeryWhen || true : false,
//     };
//   };

//   const historyData = mapPreScreenToHistory(storedHistory) || patient?.history || {};

//   const toggle = (item) => {
//     setSelected((prev) => {
//       const next = new Set(prev);
//       next.has(item) ? next.delete(item) : next.add(item);
//       return next;
//     });
//   };

//   const selectedArr = [...selected];

//   return (
//     <StepLayout
//       title="Symptoms Cluster"
//       subtitle="Please select all the symptoms that apply to you"
//       icon="🩺"
//       footer={
//         <NavFooter
//           onBack={onBack}
//           onNext={() => onNext(selectedArr)}
//           nextLabel="Save & Next"
//           disableNext={selected.size === 0}
//         />
//       }
//     >
//       <div className={styles.wrapper}>

//         {/* LEFT */}
//         <div className={styles.left}>
//           {/* <div className={styles.card}>
//             <h3 className={styles.title}>
//               Please check all the statements that apply to you
//             </h3>

//             <div className={styles.chipContainer}>
//               {SYMPTOMS.map((s) => (
//                 <Chip
//                   key={s}
//                   label={s}
//                   selected={selected.has(s)}
//                   onClick={() => toggle(s)}
//                 />
//               ))}
//             </div>
//           </div> */}
//           <div className={styles.card}>
//             <h3 className={styles.title}>
//               Select symptoms that best describe your condition
//             </h3>

//             {SYMPTOM_CLUSTERS.map((group) => (
//               <div key={group.title} className={styles.cluster}>

//                 <p className={styles.clusterTitle}>{group.title}</p>

//                 <div className={styles.chipContainer}>
//                   {group.items.map((item) => (
//                     <Chip
//                       key={item}
//                       label={item}
//                       selected={selected.has(item)}
//                       onClick={() => toggle(item)}
//                     />
//                   ))}
//                 </div>

//               </div>
//             ))}
//           </div>
//         </div>


//         <div className={styles.right}>
//           <PatientCard patient={patient} />

//           <div className={styles.historyCard}>
//             <h4 className={styles.historyTitle}>Medical History</h4>

//             <ul className={styles.historyList}>
//               {Object.entries(historyData).length > 0 ? (
//                 Object.entries(historyData).map(([key, value], i) => {
//                   let label = "";
//                   let icon = "?";

//                   switch (key) {
//                     case "condition":
//                       label = value && value !== true ? `Diagnosed with ${value}` : "Has medical condition";
//                       icon = value ? "✔" : "✖";
//                       break;

//                     case "takingMeds":
//                       label = "Currently taking medications";
//                       icon = value ? "✔" : "✖";
//                       break;

//                     case "allergy":
//                       label = "Has allergies";
//                       icon = value === "Yes" ? "✔" : value === "No" ? "✖" : "?";
//                       break;

//                     case "surgery":
//                       label = value && value !== true ? `Surgery: ${value}` : "Past surgery";
//                       icon = value ? "✔" : "✖";
//                       break;

//                     default:
//                       label = key;
//                   }

//                   return (
//                     <li key={i} className={styles.historyItem}>
//                       <span className={styles.historyDot}>{icon}</span>
//                       {label}
//                     </li>
//                   );
//                 })
//               ) : (
//                 <li className={styles.historyEmpty}>No history available</li>
//               )}
//             </ul>
//           </div>
//         </div>

//       </div>
//     </StepLayout>
//   );
// }

